// Auto-submit controller — submits the enclosing form on change or debounced input
// Usage:
//   change:    data-action="change->ui--auto-submit#submit"
//   search:    data-controller="ui--auto-submit" data-action="input->ui--auto-submit#search"
//   cancel:    data-action="submit->ui--auto-submit#cancel" (drop the pending
//              debounce when Enter submits the form natively)
//   delay:     data-ui--auto-submit-delay-value="500" (default: 300ms)
//   serialize: data-ui--auto-submit-serialize-value="true"
//
// Serialized mode (CONEK-4572): when multiple independent auto-submit forms can
// fire in quick succession against the SAME resource — e.g. the ticket-detail
// sidebar where urgency / impact / category / assignee are each their own form
// hitting PATCH /quick_update — two overlapping requests race: each rebuilds its
// turbo-stream from the resource state at its own read time, and a field write
// (and the derived priority) can be silently lost. Opting a form into
// `serialize: true` makes all serialized submits queue behind one another via a
// shared promise chain, so requests never overlap. Default (unserialized) mode is
// unchanged — search boxes and filters keep firing freely.

import { Controller } from "@hotwired/stimulus"

// Shared across every controller instance in the document. A serialized submit
// links onto the tail of this chain and only fires once the previous one's
// `turbo:submit-end` has resolved.
let serializedChain = Promise.resolve()

export default class extends Controller {
  static values = {
    delay: { type: Number, default: 300 },
    serialize: { type: Boolean, default: false },
  }

  submit() {
    if (this.serializeValue) {
      this._enqueueSerialized()
      return
    }
    this._submitNow()
  }

  search() {
    clearTimeout(this._timeout)
    this._timeout = setTimeout(() => {
      this.element.requestSubmit()
    }, this.delayValue)
  }

  // Drop a pending debounced submit. Wire it on the form as
  // `submit->ui--auto-submit#cancel` alongside a debounced `search()` input:
  // pressing Enter submits natively, and without this the timer armed by the
  // last keystroke fires a second, identical request right after.
  cancel() {
    clearTimeout(this._timeout)
  }

  _submitNow() {
    // Blur the focused control so Turbo can't restore focus on the new
    // partial after the turbo-stream response. Without this, components like
    // Tom Select see focus on their re-created input and re-open the dropdown.
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur()
    }
    this.element.requestSubmit()
  }

  // Chain this submit after any in-flight serialized submit. Returns a promise
  // that resolves when THIS form's turbo submission ends, so the next queued
  // submit waits for it.
  _enqueueSerialized() {
    serializedChain = serializedChain.then(() => this._submitAndWait())
    // Swallow rejections so one failed submit doesn't break the chain for the next.
    serializedChain = serializedChain.catch(() => {
      /* keep the chain alive after a failed/aborted submit */
    })
  }

  _submitAndWait() {
    return new Promise((resolve) => {
      const form = this.element
      const onEnd = () => {
        form.removeEventListener("turbo:submit-end", onEnd)
        resolve()
      }
      form.addEventListener("turbo:submit-end", onEnd)
      this._submitNow()
      // Safety net: if no turbo:submit-end arrives (e.g. the form was detached
      // by its own turbo-stream replace), release the chain after a short delay
      // so a missed event can't deadlock subsequent submits.
      setTimeout(onEnd, 5000)
    })
  }

  disconnect() {
    clearTimeout(this._timeout)
  }
}
